/**
 * AddedToCartBanner
 *
 * Mobile-only (lg:hidden) bottom snackbar that appears briefly when an item is
 * added to the cart. On mobile we skip the full-screen drawer opening (which
 * feels intrusive) and instead show this lightweight confirmation that lets
 * users continue browsing or jump straight to the cart.
 *
 * Positioning mirrors StickyBuyBar — fixed just above the mobile bottom nav.
 */

import { useEffect, useRef } from 'react';
import { ShoppingCart, X, ArrowRight } from 'lucide-react';
import { useCartStore } from '@/stores/useCartStore';
import { cn } from '@/lib/utils';

const BANNER_DURATION_MS = 4000;

export const AddedToCartBanner = () => {
    const { lastAddedItem, clearLastAddedItem, openDrawer } = useCartStore();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Auto-dismiss after BANNER_DURATION_MS whenever a new item is set
    useEffect(() => {
        if (lastAddedItem) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(clearLastAddedItem, BANNER_DURATION_MS);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [lastAddedItem, clearLastAddedItem]);

    const handleViewCart = () => {
        clearLastAddedItem();
        openDrawer();
    };

    const isVisible = lastAddedItem !== null;

    return (
        // Outer wrapper: always present in DOM, never intercepts touches when hidden
        <div
            aria-live="polite"
            aria-atomic="true"
            className="fixed bottom-16 inset-x-0 z-40 lg:hidden px-3 pointer-events-none"
        >
            <div
                role="status"
                className={cn(
                    // Layout
                    'flex items-center gap-3 px-4 py-3',
                    // Visual
                    'bg-foreground text-background rounded-xl shadow-2xl',
                    // Animation
                    'transition-all duration-300',
                    // Visibility toggling
                    isVisible
                        ? 'translate-y-0 opacity-100 pointer-events-auto [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]'
                        : 'translate-y-full opacity-0 duration-200 [transition-timing-function:cubic-bezier(0.4,0,1,1)]'
                )}
            >
                {/* Thumbnail or fallback icon */}
                <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-white/10">
                    {lastAddedItem?.image ? (
                        <img
                            src={lastAddedItem.image}
                            alt=""
                            aria-hidden="true"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <ShoppingCart className="h-5 w-5 text-white/60" aria-hidden="true" />
                        </div>
                    )}
                </div>

                {/* Label + product name */}
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50">
                        Added to cart
                    </p>
                    <p className="text-sm font-semibold text-white truncate">
                        {lastAddedItem?.name ?? ''}
                    </p>
                </div>

                {/* View cart CTA */}
                <button
                    type="button"
                    onClick={handleViewCart}
                    className="flex-shrink-0 flex items-center gap-1 text-sm font-semibold text-white/90 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1"
                    aria-label="View cart"
                    tabIndex={isVisible ? 0 : -1}
                >
                    View cart
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>

                {/* Dismiss */}
                <button
                    type="button"
                    onClick={clearLastAddedItem}
                    className="flex-shrink-0 p-1 text-white/40 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded"
                    aria-label="Dismiss"
                    tabIndex={isVisible ? 0 : -1}
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export default AddedToCartBanner;
